import axios from 'axios'

import { getAuthToken, clearAuthToken } from './auth-token'

import type { ApiErrorDetail, ApiResponse } from '@company/shared'
import type { AxiosError, AxiosResponse } from 'axios'

/**
 * API 错误类
 */
export class ApiError extends Error {
  code: string
  details?: Record<string, unknown>

  constructor(error: ApiErrorDetail) {
    super(error.message)
    this.name = 'ApiError'
    this.code = error.code
    this.details = error.details
  }
}

/**
 * 离线错误类
 */
export class OfflineError extends Error {
  constructor() {
    super('您当前处于离线状态，无法执行此操作')
    this.name = 'OfflineError'
  }
}

/**
 * API 基础 URL
 */
const API_BASE_URL = '/api/v1'

/**
 * Axios 实例
 */
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

/**
 * 检查是否在线
 */
function checkOnline(): void {
  if (!navigator.onLine) {
    throw new OfflineError()
  }
}

/**
 * 请求拦截器
 */
api.interceptors.request.use(
  (config) => {
    // 添加请求 ID
    config.headers['X-Request-ID'] = crypto.randomUUID()

    // 添加 Bearer Token
    const token = getAuthToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 对于写操作，检查网络状态
    const method = config.method?.toUpperCase()
    if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      checkOnline()
    }

    return config
  },
  (error: Error) => {
    return Promise.reject(error)
  }
)

/**
 * 响应拦截器
 */
api.interceptors.response.use(
  (response: AxiosResponse<ApiResponse<unknown>>) => {
    return response
  },
  (error: AxiosError<{ error: ApiErrorDetail }>) => {
    // 处理 401 未认证
    if (error.response?.status === 401) {
      // 清除本地 token
      clearAuthToken()
      // 只有不在登录页时才重定向，避免循环
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login?error=session_expired'
      }
    }

    // 提取错误信息
    const errorDetail = error.response?.data?.error || {
      code: 'NETWORK_ERROR',
      message: error.message || '网络错误',
    }

    return Promise.reject(new ApiError(errorDetail))
  }
)

/**
 * GET 请求
 */
export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, { params })
  if (response.data.success) {
    return response.data.data
  }
  throw new ApiError(response.data.error)
}

/**
 * GET 请求（分页响应）
 * 返回包含 data 和 meta 的完整响应
 * 支持两种后端响应格式:
 * 1. { success: true, data: [...], meta: {...} }
 * 2. { success: true, data: { data: [...], total, page, pageSize, totalPages } }
 */
export async function getPaginated<T>(
  url: string,
  params?: Record<string, unknown>
): Promise<{
  data: T[]
  meta: { page: number; pageSize: number; total: number; totalPages: number }
}> {
  const response = await api.get<
    ApiResponse<
      T[] | { data: T[]; total: number; page: number; pageSize: number; totalPages: number }
    > & { meta?: { page: number; pageSize: number; total: number; totalPages: number } }
  >(url, { params })
  if (response.data.success) {
    const responseData = response.data.data
    // Handle nested data structure: { data: [...], total, page, pageSize, totalPages }
    if (
      responseData &&
      typeof responseData === 'object' &&
      'data' in responseData &&
      Array.isArray(responseData.data)
    ) {
      return {
        data: responseData.data,
        meta: {
          page: responseData.page ?? 1,
          pageSize: responseData.pageSize ?? 20,
          total: responseData.total ?? 0,
          totalPages: responseData.totalPages ?? 0,
        },
      }
    }
    // Handle flat structure: data is array, meta is separate
    return {
      data: responseData as T[],
      meta: response.data.meta ?? { page: 1, pageSize: 20, total: 0, totalPages: 0 },
    }
  }
  throw new ApiError(response.data.error)
}

/**
 * POST 请求
 */
export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, data)
  if (response.data.success) {
    return response.data.data
  }
  throw new ApiError(response.data.error)
}

/**
 * PUT 请求
 */
export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.put<ApiResponse<T>>(url, data)
  if (response.data.success) {
    return response.data.data
  }
  throw new ApiError(response.data.error)
}

/**
 * PATCH 请求
 */
export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, data)
  if (response.data.success) {
    return response.data.data
  }
  throw new ApiError(response.data.error)
}

/**
 * DELETE 请求
 */
export async function del<T>(url: string): Promise<T> {
  const response = await api.delete<ApiResponse<T>>(url)
  if (response.data.success) {
    return response.data.data
  }
  throw new ApiError(response.data.error)
}

/**
 * GET 请求（返回完整响应，用于包含 meta/summary 等额外字段的端点）
 * 适用于 my-tasks、workload-stats 等返回额外字段的 API
 */
export async function getWithExtras<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await api.get<T & { success: boolean; error?: ApiErrorDetail }>(url, { params })
  if (response.data.success) {
    return response.data
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  throw new ApiError(response.data.error!)
}
